import styled from 'styled-components';
import {rgba} from 'polished';
import React, {FC} from 'react';

import {palette, fontSizesMixins, fontWeights} from '../../lib/styleVariables';
import {FilterCategoryItem} from './filter';
import {Icon, IconName} from './icon';
import {Checkbox} from './inputs';

const StyledFiltersWrapper = styled.div`
  background: ${palette.white.base};
  width: 100%;
  padding: 8px 8px 0;
  display: flex;
  flex-direction: column;

  &:first-child {
    border-radius: 10px 10px 0px 0px;
  }
  &:last-child {
    border-radius: 0px 0px 10px 10px;
    padding-bottom: 8px;
  }
  &:only-child {
    border-radius: 10px;
  }
`;

const StyledFilterHeaderButton = styled.button`
  outline: 0;
  border: 0;
  cursor: pointer;
  padding: 0;
  appearance: none;
  border-radius: 8px;
  height: 48px;
  background-color: ${palette.white.base};

  &:hover {
    background-color: ${rgba('black', 0.08)};
  }
`;

const StyledFilterContentSpan = styled.span`
  display: flex;
  align-items: center;
  padding: 0 16px;
`;

const StyledFilterHeaderTitle = styled.span`
  ${fontSizesMixins.h3()}
  flex-grow: 1;
  text-align: left;
  font-weight: ${fontWeights.normal};
  color: ${palette.black.base};
`;

const StyledFilterCategoryUl = styled.ul<{hasHorizontalPadding?: boolean}>`
  border-radius: 8px;
  list-style-type: none;
  margin: 0;
  padding: 8px ${props => (props.hasHorizontalPadding ? '8px' : 0)};
`;

const StyledFilterCategoryListCheckbox = styled(Checkbox)`
  padding: 0 8px;
  min-height: 32px;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background-color: ${rgba('black', 0.08)};
  }
`;

export interface FilterCategoryProps {
  categoryKey: string;
  categoryTitle: string;
  items: FilterCategoryItem[];
  isExpanded?: boolean;
  onFilterChange: (item: FilterCategoryItem, checked: boolean) => void;
  onToggle: (isExpanded: boolean) => void;
  selectedItemKeys: string[];
  itemRenderer?: (item: FilterCategoryItem, checked: boolean) => React.ReactElement;
}

export const FilterCategory: FC<FilterCategoryProps> = props => {
  const {isExpanded, onToggle} = props;
  const icon: IconName = isExpanded ? 'collapse' : 'expand';
  const onItemChange = (e: React.FormEvent<HTMLInputElement>, item: FilterCategoryItem) => {
    const {target} = e;
    if (props.onFilterChange && target instanceof HTMLInputElement)
      props.onFilterChange(item, target.checked);
  };
  const onToggleItem = (item: FilterCategoryItem) => {
    props.onFilterChange(item, !props.selectedItemKeys.includes(item.key));
  };

  const itemRendererIsFunction = typeof props.itemRenderer === 'function';

  return (
    <StyledFiltersWrapper>
      <StyledFilterHeaderButton onClick={() => onToggle(!isExpanded)}>
        <StyledFilterContentSpan>
          <StyledFilterHeaderTitle>{props.categoryTitle}</StyledFilterHeaderTitle>
          <Icon name={icon} />
        </StyledFilterContentSpan>
      </StyledFilterHeaderButton>
      {isExpanded && props.items && (
        <StyledFilterCategoryUl hasHorizontalPadding={!itemRendererIsFunction}>
          {props.items.map(item =>
            (props.itemRenderer && itemRendererIsFunction ? (
              // eslint-disable-next-line
              <li key={item.key} onClick={() => onToggleItem(item)}>
                {props.itemRenderer(item, props.selectedItemKeys.includes(item.key))}
              </li>
            ) : (
              <li key={item.key}>
                <StyledFilterCategoryListCheckbox
                  checked={props.selectedItemKeys.indexOf(item.key) > -1}
                  onChange={e => onItemChange(e, item)}
                  label={item.title}
                  number={item.number}
                />
              </li>
            ))
          )}
        </StyledFilterCategoryUl>
      )}
    </StyledFiltersWrapper>
  );
};
